//////////////////////////////////////////////////////////////////////////////
//
//  포인트 거래 내역 목록 화면
//
//  작성자: MediInStep Team
//  생성일: 2025-12-21
//
//  설명: 포인트 거래 내역 목록 조회, 페이징 기능
//
//////////////////////////////////////////////////////////////////////////////

import { Component } from "react";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Collapse from "@mui/material/Collapse";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";
import BasicTable from "ScreenLayouts/TableLayout/BasicTableLayout/BasicTable";

// Network
import { request } from "app/network";
import { useMaterialUIController } from "context";
import { formatLocalDateTime, formatLocalDate, formatLocalTime } from "utils/dateFormatter";

interface PointTransactionListState {
    transactions: any[];
    pageSize: number;
    pageNumber: number;
    totalCount: number;
    loading: boolean;
    // Search filters
    showFilters: boolean;
    userName: string;
    transactionType: string;
    startDate: string;
    endDate: string;
    searchText: string;
}

const PAGE_SIZE_STORAGE_KEY = "mediinstep_point_transaction_list_page_size";

class PointTransactionListScreenClass extends Component<any, PointTransactionListState> {
    constructor(props: any) {
        super(props);
        const savedPageSize = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        const initialPageSize = savedPageSize ? parseInt(savedPageSize, 10) : 10;

        this.state = {
            transactions: [],
            pageSize: initialPageSize,
            pageNumber: 1,
            totalCount: 0,
            loading: false,
            // Search filters
            showFilters: false,
            userName: "",
            transactionType: "",
            startDate: "",
            endDate: "",
            searchText: "",
        };
    }

    componentDidMount() {
        this.loadTransactions();
    }

    loadTransactions = async () => {
        try {
            this.setState({ loading: true });

            const param = {
                PageNumber: this.state.pageNumber,
                PageSize: this.state.pageSize,
                UserName: this.state.userName || undefined,
                TransactionType: this.state.transactionType || undefined,
                StartDate: this.state.startDate || undefined,
                EndDate: this.state.endDate || undefined,
                SearchText: this.state.searchText || undefined,
            };

            const res = await request("PointTransaction.pagedList", { paramz: param });

            if (res && res.data) {
                const pagedList = res.data;
                const transactions = (pagedList.items || []).map((tx: any) => {
                    const transactionDate = tx.crtDt || tx.transactionDate || null;

                    return {
                        id: tx.seqId,
                        transactionDate,
                        userName: tx.userName || "-",
                        transactionType: tx.transactionType,
                        pointAmount: tx.pointAmount?.toLocaleString() || "0",
                        balanceAfter: tx.balanceAfter?.toLocaleString() || "0",
                        reason: tx.reason || "-",
                        description: tx.description || "-",
                    };
                });

                this.setState({
                    transactions,
                    totalCount: pagedList.totalCount || 0,
                    loading: false,
                });
            } else {
                this.setState({ loading: false });
            }
        } catch (error: any) {
            console.error("포인트 거래 내역 목록 조회 실패:", error);
            this.setState({ loading: false });
        }
    };

    handlePageChange = (pageIndex: number) => {
        this.setState({ pageNumber: pageIndex + 1 }, () => {
            this.loadTransactions();
        });
    };

    handlePageSizeChange = (pageSize: number) => {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, pageSize.toString());
        this.setState({ pageSize, pageNumber: 1 }, () => {
            this.loadTransactions();
        });
    };

    handleSearch = () => {
        this.setState({ pageNumber: 1 }, () => {
            this.loadTransactions();
        });
    };

    handleClearFilters = () => {
        this.setState({
            userName: "",
            transactionType: "",
            startDate: "",
            endDate: "",
            searchText: "",
            pageNumber: 1,
        }, () => {
            this.loadTransactions();
        });
    };

    render() {
        const { transactions, pageSize, loading, totalCount, pageNumber } = this.state;

        const renderDateCell = (value: string | null) => {
            if (!value || value === "-") return "-";

            return (
                <MDBox lineHeight={1.2} textAlign="center">
                    <MDTypography variant="caption" display="block" color="text" fontWeight="medium">
                        {formatLocalDate(value)}
                    </MDTypography>
                    <MDTypography variant="caption" display="block" color="text">
                        {formatLocalTime(value)}
                    </MDTypography>
                </MDBox>
            );
        };

        const columns = [
            {
                Header: "거래일시",
                accessor: "transactionDate",
                align: "center",
                width: "15%",
                Cell: ({ row }: any) => renderDateCell(row.original.transactionDate)
            },
            { Header: "사용자명", accessor: "userName", align: "left", width: "12%" },
            {
                Header: "타입",
                accessor: "transactionType",
                align: "center",
                width: "8%",
                Cell: ({ row }: any) => {
                    const type = row.original.transactionType;

                    let badgeColor: "success" | "error" | "warning" | "info" = "error";
                    let badgeText = "사용";

                    if (type === "EARN") {
                        badgeColor = "success";
                        badgeText = "적립";
                    } else if (type === "REFUND") {
                        badgeColor = "warning";
                        badgeText = "환불";
                    }

                    return (
                        <MDBadge
                            badgeContent={badgeText}
                            color={badgeColor}
                            variant="gradient"
                            size="sm"
                        />
                    );
                }
            },
            {
                Header: "포인트",
                accessor: "pointAmount",
                align: "right",
                width: "10%",
                Cell: ({ row }: any) => {
                    const type = row.original.transactionType;
                    const isPositive = type === "EARN" || type === "REFUND";
                    const color = isPositive ? "success" : "error";

                    return (
                        <MDTypography variant="h6" fontWeight="bold" color={color}>
                            {isPositive ? "+" : "-"}{row.original.pointAmount}
                        </MDTypography>
                    );
                }
            },
            { Header: "거래 후 잔액", accessor: "balanceAfter", align: "right", width: "10%" },
            { Header: "사유", accessor: "reason", align: "left", width: "15%" },
            { Header: "설명", accessor: "description", align: "left", width: "20%" },
        ];

        const rows = transactions;

        const totalPages = Math.ceil(totalCount / pageSize);
        const pageCountArray = Array.from({ length: totalPages }, (_, i) => i);
        const currentPageIndex = pageNumber - 1;
        const pageTenNumber = Math.floor(currentPageIndex / 10) * 10;

        const paginationCriteria = {
            PageNumber: currentPageIndex,
            PageCount: pageCountArray,
            PageTenNumber: pageTenNumber,
            totalCount: totalCount,
        };

        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3} position="relative">
                    <Card sx={{ "& table": { tableLayout: "fixed", width: "100%" } }}>
                        <MDBox p={3} lineHeight={1}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                <MDBox display={"flex"} alignContent="center">
                                    <Icon fontSize={"medium"}>receipt_long</Icon>
                                    <MDTypography ml={1} variant="h5" fontWeight="medium">
                                        포인트 거래 내역 ( POINT TRANSACTIONS )
                                    </MDTypography>
                                </MDBox>
                                <MDButton
                                    variant="outlined"
                                    color="info"
                                    size="small"
                                    onClick={() => this.setState({ showFilters: !this.state.showFilters })}
                                >
                                    <Icon>filter_list</Icon>&nbsp;필터
                                </MDButton>
                            </MDBox>
                            <MDTypography variant="button" color="text" mt={1}>
                                포인트 거래 내역 목록 (전체: {totalCount}개)
                            </MDTypography>

                            {/* Filter Section */}
                            <Collapse in={this.state.showFilters}>
                                <MDBox mt={3} p={2} sx={{ bgcolor: this.props.darkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)", borderRadius: 2 }}>
                                    <MDBox display="flex" gap={2} flexWrap="wrap" alignItems="center">
                                        <TextField
                                            label="사용자명"
                                            value={this.state.userName}
                                            onChange={(e) => this.setState({ userName: e.target.value })}
                                            size="small"
                                            sx={{ minWidth: 200, height: 40 }}
                                        />
                                        <TextField
                                            select
                                            label="거래 타입"
                                            value={this.state.transactionType}
                                            onChange={(e) => this.setState({ transactionType: e.target.value })}
                                            size="small"
                                            sx={{ minWidth: 150 }}
                                            InputProps={{
                                                style: { height: 40 }
                                            }}
                                        >
                                            <MenuItem value="">전체</MenuItem>
                                            <MenuItem value="EARN">적립</MenuItem>
                                            <MenuItem value="USE">사용</MenuItem>
                                            <MenuItem value="REFUND">환불</MenuItem>
                                        </TextField>
                                        <TextField
                                            label="시작일"
                                            type="date"
                                            value={this.state.startDate}
                                            onChange={(e) => this.setState({ startDate: e.target.value })}
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ minWidth: 180, height: 40 }}
                                        />
                                        <TextField
                                            label="종료일"
                                            type="date"
                                            value={this.state.endDate}
                                            onChange={(e) => this.setState({ endDate: e.target.value })}
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ minWidth: 180, height: 40 }}
                                        />
                                        <TextField
                                            label="사유/설명 검색"
                                            value={this.state.searchText}
                                            onChange={(e) => this.setState({ searchText: e.target.value })}
                                            size="small"
                                            sx={{ minWidth: 200, height: 40 }}
                                        />
                                    </MDBox>
                                    <MDBox display="flex" gap={1} mt={2}>
                                        <MDButton
                                            variant="gradient"
                                            color="info"
                                            size="small"
                                            onClick={this.handleSearch}
                                        >
                                            <Icon>search</Icon>&nbsp;검색
                                        </MDButton>
                                        <MDButton
                                            variant="outlined"
                                            color="secondary"
                                            size="small"
                                            onClick={this.handleClearFilters}
                                        >
                                            <Icon>clear</Icon>&nbsp;초기화
                                        </MDButton>
                                    </MDBox>
                                </MDBox>
                            </Collapse>
                        </MDBox>
                        <BasicTable
                            canSearch={false}
                            criteria={paginationCriteria}
                            storePageSize={pageSize}
                            goPage={this.handlePageChange}
                            setStorePageSize={this.handlePageSizeChange}
                            table={{ columns, rows }}
                            isSorted={false}
                            entriesPerPage={{
                                defaultValue: pageSize,
                                entries: [5, 10, 15, 20, 25, 30, 40, 50],
                            }}
                            showTotalEntries={true}
                            noEndBorder
                        />
                        {loading && (
                            <MDBox
                                position="absolute"
                                top={0}
                                left={0}
                                right={0}
                                bottom={0}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                bgcolor="rgba(0, 0, 0, 0.3)"
                                sx={{ pointerEvents: "none" }}
                            >
                                <MDBox display="flex" gap={1.5}>
                                    <CircularProgress size={40} sx={{ color: "#49a3f1" }} />
                                </MDBox>
                            </MDBox>
                        )}
                    </Card>
                </MDBox>
                <Footer />
            </DashboardLayout>
        );
    }
}

function PointTransactionListScreen(props: any) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    return <PointTransactionListScreenClass {...props} darkMode={darkMode} />;
}

export default PointTransactionListScreen;
